import { create } from "zustand"
import type { SignedImageResponse } from "@/shared/types/api"

interface CachedImage extends SignedImageResponse {
  fetchedAt: number // Timestamp when the URL was fetched
}

interface ImageState {
  // Cache: imageId -> CachedImage
  cache: Map<string, CachedImage>

  // Add images to cache
  setImages: (images: SignedImageResponse[]) => void

  // Get a single image from cache
  getImage: (imageId: string) => CachedImage | null

  // Get multiple images from cache
  getImages: (imageIds: string[]) => Map<string, CachedImage>

  // Check if URLs need refresh (less than 10 minutes remaining)
  needsRefresh: (imageId: string) => boolean

  // Clear cache
  clearCache: () => void

  // Remove specific images from cache
  removeImages: (imageIds: string[]) => void
}

const REFRESH_THRESHOLD_MS = 10 * 60 * 1000 // 10 minutes in milliseconds

export const useImageStore = create<ImageState>((set, get) => ({
  cache: new Map(),

  setImages: (images) => {
    const now = Date.now()
    set((state) => {
      const newCache = new Map(state.cache)
      images.forEach((image) => {
        newCache.set(image.imageId, {
          ...image,
          fetchedAt: now,
        })
      })
      return { cache: newCache }
    })
  },

  getImage: (imageId) => {
    return get().cache.get(imageId) || null
  },

  getImages: (imageIds) => {
    const cache = get().cache
    const result = new Map<string, CachedImage>()
    imageIds.forEach((id) => {
      const image = cache.get(id)
      if (image) {
        result.set(id, image)
      }
    })
    return result
  },

  needsRefresh: (imageId) => {
    const image = get().cache.get(imageId)
    if (!image) return true

    // Parse expiresAt timestamp
    const expiresAt = new Date(image.expiresAt).getTime()
    const now = Date.now()
    const timeUntilExpiry = expiresAt - now

    // Refresh if less than 10 minutes remaining
    return timeUntilExpiry < REFRESH_THRESHOLD_MS
  },

  clearCache: () => {
    set({ cache: new Map() })
  },

  removeImages: (imageIds) => {
    set((state) => {
      const newCache = new Map(state.cache)
      imageIds.forEach((id) => newCache.delete(id))
      return { cache: newCache }
    })
  },
}))
